import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import FramedAvatar from '../components/FramedAvatar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Activity,
  Trophy,
  Lightbulb,
  Award,
  TrendingUp,
  UserPlus,
  Heart,
  ThumbsUp,
  MessageCircle,
  RefreshCw
} from 'lucide-react';

const CommunityFeedPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchFeed();
  }, []);

  const fetchFeed = async () => {
    try {
      const response = await api.get('/community/feed');
      setFeed(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchFeed();
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'victory':
        return <Trophy className="w-4 h-4 text-amber-500" />;
      case 'idea':
        return <Lightbulb className="w-4 h-4 text-blue-500" />;
      case 'badge':
        return <Award className="w-4 h-4 text-purple-500" />;
      case 'level_up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'new_member':
        return <UserPlus className="w-4 h-4 text-cyan-500" />;
      default:
        return <Activity className="w-4 h-4 text-primary" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'victory':
        return 'bg-amber-500/10 border-amber-500/30';
      case 'idea':
        return 'bg-blue-500/10 border-blue-500/30';
      case 'badge':
        return 'bg-purple-500/10 border-purple-500/30';
      case 'level_up':
        return 'bg-green-500/10 border-green-500/30';
      case 'new_member':
        return 'bg-cyan-500/10 border-cyan-500/30';
      default:
        return 'bg-primary/10 border-primary/30';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'victory':
        return 'Victoire';
      case 'idea':
        return 'Idée';
      case 'badge':
        return 'Badge';
      case 'level_up':
        return 'Level Up';
      case 'new_member':
        return 'Nouveau membre';
      default:
        return 'Activité';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    if (hours < 24) return `Il y a ${hours}h`;
    if (days < 7) return `Il y a ${days}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const handleItemClick = (item) => {
    switch (item.type) {
      case 'victory':
        navigate('/victories');
        break;
      case 'idea':
        navigate('/ideas');
        break;
      case 'badge':
      case 'level_up':
        navigate('/leaderboard');
        break;
      case 'new_member':
        navigate('/members-map');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Activity className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement du fil d'actualité...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="community-feed-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                <Activity className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="font-heading font-bold text-lg sm:text-xl truncate">Fil d'actualité</h1>
                <p className="text-xs sm:text-sm text-muted-foreground truncate">L'activité de la communauté</p>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-feed-btn"
            className="flex-shrink-0 px-2 sm:px-3"
          >
            <RefreshCw className={`w-4 h-4 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualiser</span>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {feed.length === 0 ? (
          <Card className="card-base text-center py-12">
            <CardContent>
              <Activity className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">Aucune activité récente</h3>
              <p className="text-muted-foreground">
                Les activités de la communauté apparaîtront ici
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {feed.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <Card 
                    className={`card-base cursor-pointer transition-all hover:scale-[1.01] border ${getTypeColor(item.type)}`}
                    onClick={() => handleItemClick(item)}
                    data-testid={`feed-item-${item.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Avatar */}
                        <FramedAvatar
                          src={item.user?.avatar_url}
                          fallback={getInitials(item.user?.first_name, item.user?.last_name)}
                          frame={item.user?.profile_frame}
                          role={item.user?.role}
                          size="lg"
                          className="flex-shrink-0"
                        />

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <span className="font-semibold">
                                {item.user?.first_name} {item.user?.last_name?.[0]}.
                              </span>
                              <span className="text-muted-foreground ml-2">
                                {item.title}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDate(item.created_at)}
                            </span>
                          </div>

                          {item.content && (
                            <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                              {item.content}
                            </p>
                          )}

                          {/* Type badge and stats */}
                          <div className="flex items-center gap-3 mt-3">
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${getTypeColor(item.type)}`}
                            >
                              {getTypeIcon(item.type)}
                              <span className="ml-1">{getTypeLabel(item.type)}</span>
                            </Badge>

                            {item.type === 'victory' && item.congratulations_count > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Heart className="w-3 h-3 text-red-500" />
                                {item.congratulations_count}
                              </span>
                            )}

                            {item.type === 'idea' && item.votes_count > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <ThumbsUp className="w-3 h-3 text-blue-500" />
                                {item.votes_count}
                              </span>
                            )}

                            {item.type === 'level_up' && item.new_level && (
                              <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">
                                Niveau {item.new_level}
                              </Badge>
                            )}

                            {item.type === 'badge' && item.badge_icon && (
                              <span className="text-lg">{item.badge_icon}</span>
                            )}

                            {item.type === 'victory' && item.victory_type && (
                              <Badge variant="secondary" className="text-xs">
                                {item.victory_type === 'first_property' ? '🏠 Premier bien' :
                                 item.victory_type === 'deal_signed' ? '✍️ Deal signé' :
                                 item.victory_type === 'rent_received' ? '💰 Loyer reçu' :
                                 item.victory_type === 'offer_accepted' ? '🎯 Offre acceptée' :
                                 item.victory_type}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
};

export default CommunityFeedPage;
